import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import {
  Search, ShoppingCart, Heart, User, Sun, Moon, Menu, X,
  Phone, MapPin, ChevronDown, LogOut, Package, Settings, Bell, Gift
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { useTheme } from '../../context/ThemeContext'
import { useAuth } from '../../context/AuthContext'
import { useCartStore } from '../../store/cartStore'
import { useWishlistStore } from '../../store/wishlistStore'
import { useNotificationStore } from '../../store/notificationStore'
import { promotionService } from '../../services/api'

export default function Header() {
  const { toggleTheme, isDark } = useTheme()
  const { user, logout, isAuthenticated, canViewAlcohol } = useAuth()
  const cartCount = useCartStore(s => s.count)
  const wishlistCount = useWishlistStore(s => s.items.length)
  const { notifications, markRead, markAllRead, clearAll, add: addNotification, unreadCount } = useNotificationStore()
  const notifCount = unreadCount()
  const [notifOpen, setNotifOpen] = useState(false)
  const notifRef = useRef(null)

  // Poll for new promotions and inject into notification bell
  const seenPromoIds = useRef(new Set())
  const { data: promoData } = useQuery({
    queryKey: ['promotions'],
    queryFn: promotionService.getMyPromos,
    enabled: isAuthenticated,
    staleTime: 2 * 60 * 1000,
    refetchInterval: isAuthenticated ? 3 * 60 * 1000 : false,
  })

  useEffect(() => {
    if (!promoData || !Array.isArray(promoData)) return
    promoData.forEach(p => {
      if (!p.dismissed && !seenPromoIds.current.has(p.id)) {
        seenPromoIds.current.add(p.id)
        if (!p.read) {
          addNotification({
            type: 'promo',
            title: p.title,
            message: p.description ? p.description.slice(0, 80) : 'Tap to view this offer',
            link: '/promotions',
            time: new Date().toISOString(),
          })
        }
      }
    })
  }, [promoData]) // eslint-disable-line
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const userMenuRef = useRef(null)

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', handler)
    return () => window.removeEventListener('scroll', handler)
  }, [])

  useEffect(() => {
    setMobileMenuOpen(false)
    setUserMenuOpen(false)
  }, [location])

  useEffect(() => {
    const handler = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  useEffect(() => {
    const handler = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleSearch = (e) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchQuery.trim())}`)
      setSearchOpen(false)
      setSearchQuery('')
    }
  }

  // ── Category dropdown — derived from the same CATEGORY_GROUPS used by the Shop sidebar ──
  // Alcoholic Beverages group
  const alcoholicItems = [
    { divider: 'Alcoholic Beverages' },
    { to: '/shop?category=beer',            label: '🍺 Beer (Mainstream)' },
    { to: '/shop?category=craft beer',      label: '🍻 Beer — Craft' },
    { to: '/shop?category=non alcoholic beer', label: '🥛 Non-Alcoholic Beer' },
    { to: '/shop?category=wine',            label: '🍷 Wine' },
    { to: '/shop?category=champagne',       label: '🥂 Champagne & Sparkling' },
    { to: '/shop?category=non alcoholic wine', label: '🍇 Non-Alcoholic Wine' },
    { to: '/shop?category=port sherry',     label: '🫙 Port, Sherry & Muscadel' },
    { to: '/shop?category=box wine',        label: '📦 Box Wine' },
    { to: '/shop?category=gin',             label: '🌿 Gin' },
    { to: '/shop?category=vodka',           label: '🍸 Vodka' },
    { to: '/shop?category=whiskey',         label: '🥃 Whiskey' },
    { to: '/shop?category=rum',             label: '🏝️ Rum' },
    { to: '/shop?category=tequila',         label: '🌵 Tequila' },
    { to: '/shop?category=brandy',          label: '🍂 Brandy' },
    { to: '/shop?category=liqueur',         label: '✨ Liqueur' },
    { to: '/shop?category=aperitif',        label: '🫗 Aperitifs' },
    { to: '/shop?category=cane',            label: '🌾 Cane' },
    { to: '/shop?category=shooters',        label: '🔫 Shooters' },
    { to: '/shop?category=spirits',         label: '🥄 Other Spirits' },
    { to: '/shop?category=cider',           label: '🍋 Ciders & RTDs' },
    { to: '/shop?category=non alcoholic rtd', label: '🧴 Non-Alcoholic RTD' },
    { to: '/shop?category=rtd cocktail',    label: '🍹 RTD Cocktails' },
  ]

  const nonAlcoholicItems = [
    { divider: 'Non-Alcoholic' },
    { to: '/shop?category=minerals',        label: '💧 Minerals' },
    { to: '/shop?category=water',           label: '🫧 Mineral Water' },
    { to: '/shop?category=mineral drinks',  label: '🧃 Mineral Drinks' },
    { to: '/shop?category=juice',           label: '🍊 Juice' },
    { to: '/shop?category=energy',          label: '⚡ Energy Drinks' },
    { to: '/shop?category=cordials',        label: '🍓 Cordials' },
  ]

  const tobaccoItems = canViewAlcohol
    ? [{ divider: 'Tobacco' }, { to: '/shop?category=cigarettes', label: '🚬 Cigarettes' }]
    : []

  const accessoriesItems = [
    { divider: 'Accessories & More' },
    { to: '/shop?category=glassware',       label: '🥂 Glassware' },
    ...(canViewAlcohol ? [{ to: '/shop?category=gifts', label: '🎁 Gifts' }] : []),
    { to: '/shop?category=snacks',          label: '🍿 Snacks' },
  ]

  const categoriesDropdown = [
    ...(canViewAlcohol ? alcoholicItems : []),
    ...nonAlcoholicItems,
    ...tobaccoItems,
    ...accessoriesItems,
  ]

  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/shop', label: 'Shop' },
    { label: 'Categories', megaCategories: true },
    { label: 'Brands', megaBrands: true },
    { to: '/contact', label: 'Contact' },
  ]

  return (
    <header className={`sticky top-0 z-50 transition-all duration-300 ${scrolled
      ? 'bg-white/95 dark:bg-gray-900/95 backdrop-blur-md shadow-md'
      : 'bg-white dark:bg-gray-900'
      }`}>
      {/* Top bar */}
      <div className="bg-primary text-white text-sm py-2 px-4 hidden lg:block">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-1.5">
              <MapPin size={13} />
              386 Johannes Ramkhoase St, Pretoria Central
            </span>
            <span className="flex items-center gap-1.5">
              <Phone size={13} />
              012 323 1520
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="animate-pulse text-gold font-medium">
              🎉 Bulk orders welcome — call us for pricing!
            </span>

          </div>
        </div>
      </div>

      {/* Main header */}
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-4">
        {/* Logo */}
        <Link to="/" className="flex-shrink-0 flex items-center group" aria-label="Tony's Liquor Distributor">
          {/* Icon-only: shows on xs, hidden on sm+ */}
          <img
            src={isDark ? "/logo-w.png" : "/logo.png"}
            alt="Tony's Liquor"
            className="h-10 w-auto sm:hidden"
          />
          {/* Full text logo: hidden on xs, shows on sm+ */}
          <img
            src={isDark ? "/logo.text-w.png" : "/logo.text.png"}
            alt="Tony's Liquor Distributor"
            className="hidden sm:block h-12 w-auto max-w-[220px] object-contain group-hover:opacity-90 transition-opacity"
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-1 ml-6">
          {navLinks.map((link) =>
            link.megaCategories ? (
              <MegaCategoriesNav
                key="categories"
                categoriesDropdown={categoriesDropdown}
                canViewAlcohol={canViewAlcohol}
              />
            ) : link.megaBrands ? (
              <MegaBrandsNav key="brands" />
            ) : link.dropdown ? (
              <DropdownNav key={link.label} item={link} />
            ) : (
              <Link
                key={link.to}
                to={link.to}
                className={`px-4 py-2 rounded-lg text-sm font-display font-medium transition-colors
                  ${location.pathname === link.to
                    ? 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-red-400'
                    : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
              >
                {link.label}
              </Link>
            )
          )}
        </nav>

        {/* Search bar - desktop */}
        <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-md ml-auto">
          <div className="relative w-full">
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search products…"
              className="input-field pr-10 text-sm"
            />
            <button type="submit" className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-primary transition-colors">
              <Search size={16} />
            </button>
          </div>
        </form>

        {/* Actions */}
        <div className="flex items-center gap-1 ml-auto md:ml-0">
          {/* Mobile search toggle */}
          <button
            onClick={() => setSearchOpen(o => !o)}
            className="md:hidden p-2.5 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <Search size={20} />
          </button>

          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            className="p-2.5 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="Toggle theme"
            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDark ? <Sun size={20} /> : <Moon size={20} />}
          </button>

          {/* Notification bell */}
          <div className="relative" ref={notifRef}>
            <button
              id="header-bell-icon"
              onClick={() => { setNotifOpen(o => !o); markAllRead() }}
              className="relative p-2.5 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Notifications"
            >
              <Bell size={20} />
              {notifCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-gold text-white text-xs rounded-full flex items-center justify-center font-bold animate-bounce-in">
                  {notifCount > 9 ? '9+' : notifCount}
                </span>
              )}
            </button>

            {notifOpen && (
              <NotificationPanel
                notifications={notifications}
                onMarkRead={markRead}
                onClearAll={clearAll}
                onClose={() => setNotifOpen(false)}
              />
            )}
          </div>

          {/* Wishlist */}
          <Link
            to="/wishlist"
            className="relative p-2.5 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <Heart size={20} />
            {wishlistCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-primary text-white text-xs rounded-full flex items-center justify-center font-bold">
                {wishlistCount > 9 ? '9+' : wishlistCount}
              </span>
            )}
          </Link>

          {/* Cart */}
          <Link
            id="header-cart-icon"
            to="/cart"
            className="relative p-2.5 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <ShoppingCart size={20} />
            {cartCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-primary text-white text-xs rounded-full flex items-center justify-center font-bold cart-badge-pulse">
                {cartCount > 99 ? '99+' : cartCount}
              </span>
            )}
          </Link>

          {/* User */}
          {isAuthenticated ? (
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(o => !o)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <div className="w-7 h-7 bg-primary/10 dark:bg-primary/20 rounded-full flex items-center justify-center">
                  <span className="text-primary dark:text-red-400 text-xs font-bold">
                    {user?.username?.[0]?.toUpperCase() || 'U'}
                  </span>
                </div>
                <span className="hidden sm:block text-sm font-medium text-gray-700 dark:text-gray-200">
                  {user?.username}
                </span>
                <ChevronDown size={14} className={`text-gray-400 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 py-1 animate-slide-down">
                  <Link to="/account" className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">
                    <User size={15} />Account
                  </Link>
                  <Link to="/orders" className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">
                    <Package size={15} />My Orders
                  </Link>
                  <Link to="/promotions" className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700" onClick={() => setUserMenuOpen(false)}>
                    <Gift size={15} className="text-primary"/>
                    Promotions
                    {promoData && Array.isArray(promoData) && promoData.filter(p => !p.read && !p.dismissed).length > 0 && (
                      <span className="ml-auto bg-primary text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                        {promoData.filter(p => !p.read && !p.dismissed).length}
                      </span>
                    )}
                  </Link>
                  <Link to="/account/settings" className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">
                    <Settings size={15} />Settings
                  </Link>
                  <hr className="my-1 border-gray-100 dark:border-gray-700" />
                  <button
                    onClick={logout}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <LogOut size={15} />Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link to="/login" className="btn-primary py-2 px-4 text-sm hidden sm:flex items-center gap-2">
              <User size={15} />Sign In
            </Link>
          )}

          {/* Mobile menu toggle */}
          <button
            onClick={() => setMobileMenuOpen(o => !o)}
            className="lg:hidden p-2.5 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors ml-1"
          >
            {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile search */}
      {searchOpen && (
        <div className="md:hidden px-4 pb-3 animate-slide-down">
          <form onSubmit={handleSearch}>
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search products…"
                className="input-field pr-10"
                autoFocus
              />
              <button type="submit" className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                <Search size={16} />
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800 animate-slide-down">
          <nav className="px-4 py-3 space-y-1">
            {navLinks.map(link =>
              link.megaCategories ? (
                <div key="categories">
                  <div className="px-4 py-2 text-sm font-display font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Categories</div>
                  {categoriesDropdown.map((sub, i) =>
                    sub.divider ? (
                      <div key={`div-${i}`} className="px-4 pt-2 pb-0.5">
                        <span className="text-[10px] font-display font-bold uppercase tracking-widest text-gray-400">{sub.divider}</span>
                      </div>
                    ) : (
                      <Link key={sub.to} to={sub.to} className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg">{sub.label}</Link>
                    )
                  )}
                </div>
              ) : link.megaBrands ? (
                <div key="brands">
                  <div className="px-4 py-2 text-sm font-display font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Brands</div>
                  <div className="px-4 py-2 flex flex-wrap gap-1.5">
                    {['0', ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')].map((letter, i) => (
                      <Link key={letter} to={`/shop?brand=${letter}`}
                        className="min-w-[28px] h-7 px-1 rounded-md text-xs font-display font-semibold bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 flex items-center justify-center hover:border-primary hover:text-primary transition-all">
                        {i === 0 ? '#0–9' : letter}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : link.dropdown ? (
                <div key={link.label}>
                  <div className="px-4 py-2 text-sm font-display font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{link.label}</div>
                  {link.dropdown.map((sub, i) =>
                    sub.divider ? (
                      <div key={`div-${i}`} className="px-4 pt-2 pb-0.5">
                        <span className="text-[10px] font-display font-bold uppercase tracking-widest text-gray-400">{sub.divider}</span>
                      </div>
                    ) : (
                      <Link key={sub.to} to={sub.to} className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg">{sub.label}</Link>
                    )
                  )}
                </div>
              ) : (
                <Link key={link.to} to={link.to}
                  className="block px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg">
                  {link.label}
                </Link>
              )
            )}
            {!isAuthenticated && (
              <Link to="/login" className="block mt-2">
                <button className="btn-primary w-full">Sign In</button>
              </Link>
            )}
          </nav>
        </div>
      )}
    </header>
  )
}

// ── Mega Categories dropdown (wide two-column) ────────────────────────────────
function MegaCategoriesNav({ categoriesDropdown, canViewAlcohol }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  const navigate = useNavigate()

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Split into left (Alcoholic) and right (everything else)
  const sections = []
  let current = null
  categoriesDropdown.forEach(item => {
    if (item.divider) { current = { title: item.divider, items: [] }; sections.push(current) }
    else if (current) current.items.push(item)
  })

  const leftSections  = sections.filter(s => s.title === 'Alcoholic Beverages')
  const rightSections = sections.filter(s => s.title !== 'Alcoholic Beverages')

  const handleNav = (to) => { navigate(to); setOpen(false) }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-display font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
      >
        Categories
        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-1 z-50 animate-slide-down
          bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700
          w-[min(92vw,680px)] flex">

          {/* Left — Alcoholic Beverages */}
          {canViewAlcohol && leftSections.map(sec => (
            <div key={sec.title} className="flex-1 min-w-0 p-4 border-r border-gray-100 dark:border-gray-700">
              <p className="text-[10px] font-display font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-2 px-1">
                {sec.title}
              </p>
              <div className="grid grid-cols-2 gap-x-1 gap-y-0.5">
                {sec.items.map(item => (
                  <button key={item.to} onClick={() => handleNav(item.to)}
                    className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-left text-xs font-display text-gray-600 dark:text-gray-300 hover:bg-primary/8 hover:text-primary dark:hover:bg-primary/15 dark:hover:text-primary transition-colors">
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          ))}

          {/* Right — Non-Alcoholic, Tobacco, Accessories */}
          <div className="w-52 shrink-0 p-4 space-y-4">
            {rightSections.map(sec => (
              <div key={sec.title}>
                <p className="text-[10px] font-display font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-1.5 px-1">
                  {sec.title}
                </p>
                <div className="space-y-0.5">
                  {sec.items.map(item => (
                    <button key={item.to} onClick={() => handleNav(item.to)}
                      className="w-full flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-left text-xs font-display text-gray-600 dark:text-gray-300 hover:bg-primary/8 hover:text-primary dark:hover:bg-primary/15 dark:hover:text-primary transition-colors">
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Mega Brands dropdown (A–Z index) ─────────────────────────────────────────
function MegaBrandsNav() {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  const navigate = useNavigate()

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const ALPHA = ['0', ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')]

  const handleBrand = (letter) => {
    navigate(`/shop?brand=${letter}`)
    setOpen(false)
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-display font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
      >
        Brands
        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-1 z-50 animate-slide-down
          bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700
          w-72 p-4">
          <p className="text-[10px] font-display font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-3">
            Browse by Brand
          </p>
          <div className="flex flex-wrap gap-1.5">
            {ALPHA.map((letter, i) => (
              <button
                key={letter}
                onClick={() => handleBrand(letter)}
                className="min-w-[32px] h-8 px-1.5 rounded-lg text-sm font-display font-semibold
                  bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600
                  text-gray-600 dark:text-gray-300
                  hover:border-primary hover:bg-primary/10 hover:text-primary
                  transition-all flex items-center justify-center"
                title={i === 0 ? 'Brands starting with 0–9' : `Brands starting with ${letter}`}
              >
                {i === 0 ? '#0–9' : letter}
              </button>
            ))}
          </div>
          <p className="text-[10px] text-gray-400 dark:text-gray-600 mt-3 font-display">
            Select a letter to filter products by brand
          </p>
        </div>
      )}
    </div>
  )
}

function DropdownNav({ item }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-display font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
      >
        {item.label}
        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 w-56 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 py-1 animate-slide-down z-50 max-h-[70vh] overflow-y-auto">
          {item.dropdown.map((sub, i) =>
            sub.divider ? (
              <div key={`div-${i}`} className="px-4 pt-3 pb-1">
                <span className="text-[10px] font-display font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                  {sub.divider}
                </span>
              </div>
            ) : (
              <Link
                key={sub.to}
                to={sub.to}
                onClick={() => setOpen(false)}
                className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-primary dark:hover:text-red-400 transition-colors"
              >
                {sub.label}
              </Link>
            )
          )}
        </div>
      )}
    </div>
  )
}

// ── Notification Panel ────────────────────────────────────────────────────────
function NotificationPanel({ notifications, onMarkRead, onClearAll, onClose }) {
  const isEmpty = notifications.length === 0

  function timeAgo(iso) {
    const secs = Math.floor((Date.now() - new Date(iso)) / 1000)
    if (secs < 60)   return 'just now'
    if (secs < 3600) return `${Math.floor(secs / 60)}m ago`
    if (secs < 86400)return `${Math.floor(secs / 3600)}h ago`
    return `${Math.floor(secs / 86400)}d ago`
  }

  const iconFor = (type) => {
    if (type === 'cart')  return '🛒'
    if (type === 'order') return '📦'
    if (type === 'promo') return '🎉'
    return '🔔'
  }

  return (
    <div className="absolute right-0 top-full mt-2 w-80 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 overflow-hidden animate-slide-down z-50">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700">
        <span className="font-display font-bold text-gray-900 dark:text-white text-sm">
          Notifications
        </span>
        {!isEmpty && (
          <button
            onClick={onClearAll}
            className="text-xs text-gray-400 hover:text-red-500 transition-colors font-display"
          >
            Clear all
          </button>
        )}
      </div>

      {/* List */}
      <div className="max-h-96 overflow-y-auto">
        {isEmpty ? (
          <div className="py-10 text-center">
            <div className="text-4xl mb-2">🔔</div>
            <p className="text-sm text-gray-400 dark:text-gray-500 font-display">No notifications yet</p>
            <p className="text-xs text-gray-300 dark:text-gray-600 mt-1">
              Add items to your cart to get started
            </p>
          </div>
        ) : (
          notifications.map(n => (
            <a
              key={n.id}
              href={n.link || '#'}
              onClick={(e) => { if (!n.link) e.preventDefault(); onMarkRead(n.id); onClose() }}
              className={`flex items-start gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors border-b border-gray-50 dark:border-gray-700/50 last:border-0 ${
                !n.read ? 'bg-primary/5 dark:bg-primary/10' : ''
              }`}
            >
              {/* Icon */}
              <div className="w-9 h-9 rounded-xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-lg flex-shrink-0 mt-0.5">
                {iconFor(n.type)}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <p className={`text-sm font-display font-semibold leading-tight ${
                    !n.read ? 'text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'
                  }`}>
                    {n.title}
                  </p>
                  {!n.read && (
                    <span className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-1" />
                  )}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
                  {n.message}
                </p>
                <p className="text-xs text-gray-300 dark:text-gray-600 mt-1">
                  {timeAgo(n.time)}
                </p>
              </div>
            </a>
          ))
        )}
      </div>

      {/* Footer */}
      {!isEmpty && (
        <div className="px-4 py-2.5 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <a
            href="/orders"
            onClick={onClose}
            className="text-xs text-primary hover:underline font-display font-medium"
          >
            View orders →
          </a>
          {notifications.some(n => n.type === 'promo') && (
            <a
              href="/promotions"
              onClick={onClose}
              className="text-xs text-gold hover:underline font-display font-medium flex items-center gap-1"
            >
              🎁 View offers →
            </a>
          )}
        </div>
      )}
    </div>
  )
}
